import React, { useState } from 'react'
import CustomModal from 'components/CustomModal/CustomModal'
import { Button, CloseButton, Spinner, FormControl, InputGroup } from 'react-bootstrap'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { useDispatch } from 'react-redux'
import DomainForm from 'components/CreateUpdateSiteModal/form/components/DomainForm'
import SiteOption from 'components/CreateUpdateSiteModal/form/components/foundations/SiteOption'
import { getDomainSuffix, getTLDs, getErrorMessage, onUpgradePlan } from 'utils/general'
import { transferRequestLinkCreate } from 'services/site'
import * as Sentry from '@sentry/react'
import { fetchUserData } from 'Manage/actions'
import { showUpgradeCardModal } from 'actions'
import { PLANS } from 'constants/plans'
import SuccessBox from './components/SuccessBox'
import { ErrorBox } from '../ErrorBox/ErrorBox'
import { STATE_SUCCESS, STATE_READY, STATE_LOADING, STATE_ERROR } from '../CreateUpdateSiteModal/interface'

export const TransferRequestLinkModal = ({ show, onHide, userProfile, customDomains, tlds = [] }) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [domainError, setDomainError] = useState(null)
  const [transferRequestLinks, setTransferRequestLinks] = useState([])
  const [pageState, setPageState] = useState(STATE_READY)
  const [createdSite, setCreatedSite] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  const dispatch = useDispatch()

  const handleCreateLink = () => {
    if (userProfile.productId !== PLANS.PRO_U.id) {
      dispatch(
        showUpgradeCardModal({
          title: 'Upgrade to Create Shared Projects',
          desc: 'Create shared projects where you can easily share & receive files to collaborate.',
          cta: 'Upgrade to Pro',
          onClick: () => onUpgradePlan({ planId: PLANS.PRO_U.id }, dispatch)
        })
      )
      return
    }

    setShowCreateForm(true)
  }

  const handleCancel = () => {
    setShowCreateForm(false)
    onHide()
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setPageState(STATE_LOADING)
      setDomainError(null)
      const formData = new FormData()

      formData.append('email', userProfile.email.toLowerCase())

      if (values.domainValues.subdomain) {
        formData.append('newDomain', `${values.domainValues.subdomain}${values.domainValues.domainSuffix}`)
      }
      if (values.domainValues.domainSuffix) {
        formData.append('domainSuffix', values.domainValues.domainSuffix)
      }

      const siteSettings = {}
      if (values.toggleValues.passwordProtected) {
        siteSettings.passwordProtected = true
        siteSettings.password = values.toggleValues.password
      }

      if (Object.keys(siteSettings).length > 0) {
        formData.append('siteSettings', JSON.stringify(siteSettings))
      }

      formData.append('isTransferRequestLink', JSON.stringify(true))

      const config = { email: userProfile.email.toLowerCase() }
      const { data } = await transferRequestLinkCreate(config, formData)

      if (data?.link) {
        setTransferRequestLinks((prevLinks) => [...prevLinks, data.link])
        setCreatedSite(data.link)
        setPageState(STATE_SUCCESS)
        fetchUserData(false)
      }
    } catch (error) {
      console.error(error)
      setPageState(STATE_ERROR)
      const errorMessage = getErrorMessage(error)
      setErrorMessage(errorMessage || 'Oops, something went wrong')
      Sentry.captureException(error, {
        tags: {
          section: 'transfer-request-link'
        }
      })
    } finally {
      setSubmitting(false)
      dispatch(fetchUserData(false))
    }
  }

  const handleRetry = () => {
    setPageState(STATE_READY)
    setErrorMessage('')
  }

  const handleClose = () => {
    setShowCreateForm(false)
    setPageState(STATE_READY)
    setCreatedSite(null)
    onHide()
  }

  const handleReset = () => {
    setShowCreateForm(true)
    setPageState(STATE_READY)
    setCreatedSite(null)
  }

  const availableTlds = getTLDs({ account: userProfile })

  const initialDomainValues = {
    domain: '',
    subdomain: '',
    domainSuffix: getDomainSuffix('').tld || availableTlds[0] || '.tiiny.site',
    customDomains: customDomains || [],
    tlds: availableTlds?.length > 0 ? availableTlds : ['.tiiny.site']
  }

  const initialUploadValues = {
    selectedFile: null,
    draggedFile: null,
    addedFiles: [],
    fileUploadLimit: 100,
    progress: 0,
    indexFile: null,
    fileType: null,
    isZipPhpFile: false
  }

  const initialToggleValues = {
    passwordProtected: false,
    password: ''
  }

  const initialValues = {
    domainValues: initialDomainValues,
    uploadValues: initialUploadValues,
    toggleValues: initialToggleValues
  }

  return (
    <CustomModal
      className="p-5"
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      {(() => {
        switch (pageState) {
          case STATE_ERROR:
            return <ErrorBox onClick={handleRetry} message={errorMessage} />
          case STATE_LOADING:
            return (
              <div className="p-4 text-center">
                <Spinner animation="border" />
                <div className="mt-3">Working magic...</div>
              </div>
            )
          case STATE_SUCCESS:
            return (
              <SuccessBox
                userProfile={userProfile}
                site={createdSite}
                files={[]}
                onClose={handleClose}
                onReset={handleReset}
              />
            )
          default:
            return !showCreateForm ? (
              <div className="p-4">
                <div className="mb-4 d-flex justify-content-between align-items-center">
                  <h2 className="bold color-purple-2 font-22 mb-0">Receive & Share Files Easily</h2>
                  <CloseButton onClick={onHide} />
                </div>

                <div className="mb-3 grey">
                  Create a shared project where you can share and also receive files from others.
                </div>

                <div className={transferRequestLinks?.length ? 'mt-4' : 'mt-5'}>
                  <Button className="float-right" variant="primary" onClick={handleCreateLink}>
                    Continue
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="mb-4 d-flex justify-content-between align-items-center">
                  <h2 className="bold color-purple-2 font-22 mb-0">Create Shared Project</h2>
                  <CloseButton onClick={handleCancel} />
                </div>

                <Formik
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                  validateOnBlur={true}
                  validateOnChange={true}
                >
                  {({ values: { domainValues, uploadValues, toggleValues }, setFieldValue }) => (
                    <Form>
                      <DomainForm
                        name={'domainValues'}
                        initialValues={domainValues}
                        userProfile={{ productId: null }}
                        action={'create'}
                        disableProFeatures={false}
                        setFieldValue={setFieldValue}
                        uploadValues={uploadValues}
                        updatingPhpFile={false}
                        setDomainError={setDomainError}
                        domainError={domainError}
                      />

                      <div className="mb-3 grey selected-file-container-transfer-request-link">
                        <img className="mt-1 op-7" src="/assets/icons/info-grey.svg" height={16} />
                        <div>
                          <div className=" grey bold">Visitors can upload files to this project.</div>
                          <div className="grey">
                            <i>You will be notified via email about new uploads.</i>
                          </div>
                        </div>
                      </div>

                      <div className="ps-3 pe-3 pb-3">
                        <div className="p-2 align-content-center m-auto">
                          <SiteOption
                            icon="lock-black.svg"
                            label="Password Protect"
                            desc="Make your link private by adding a layer of security"
                            onChange={() =>
                              setFieldValue('toggleValues.passwordProtected', !toggleValues.passwordProtected)
                            }
                            checked={toggleValues.passwordProtected}
                          />
                        </div>
                      </div>

                      {toggleValues.passwordProtected && (
                        <div className="ps-3 pe-3 pb-3">
                          <InputGroup className="flex" size="sm">
                            <InputGroup.Text>Password</InputGroup.Text>
                            <Field
                              name="toggleValues.password"
                              value={toggleValues.password}
                              type="text"
                              as={FormControl}
                              placeholder="Enter password"
                              aria-label="Password"
                              aria-describedby="inputGroupPrepend"
                            />
                          </InputGroup>
                          <ErrorMessage
                            name="toggleValues.password"
                            component="div"
                            className="validation-error text-center"
                          />
                        </div>
                      )}
                      <div className="mt-4 d-flex justify-content-end">
                        <Button className="w-100" variant="primary" type="submit" disabled={domainError !== null}>
                          Create Share Project
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            )
        }
      })()}
    </CustomModal>
  )
}
